import * as THREE from 'three';

// Held at module level: this runs on every mousemove while a planet is open, and none of these
// three outlives the call.
const _ray = new THREE.Ray();
const _sphere = new THREE.Sphere();
const _toCard = new THREE.Vector3();
const _entry = new THREE.Vector3();

/**
 * Whether the planet is standing between the camera and a card hanging on it.
 *
 * The cards sit on a ring right around the planet — `resetImagePositions` puts them on a
 * circle of 1.1 radii in the planet's own frame — so from wherever the camera lands, part of
 * that ring is behind the sphere. A raycast handed the cards alone cannot tell: it hits a
 * hidden card exactly as readily as a visible one, which is how clicking apparently empty
 * planet opened a work standing behind it.
 *
 * Both ends are taken in world space, and the card end is the point the ray actually met
 * rather than the card's centre, so the question asked is the one the hit already answers:
 * does any of the planet lie on the segment between the camera and this point.
 *
 * three is asked for the sphere's near surface instead of the planet's mesh being put in the
 * raycast's target list. Adding it as a target would settle the same question — the nearest
 * hit would win — but a ray that meets the sphere would then walk its ~8000 faces to find
 * that out, on every mousemove, and this scene is deliberately not doing that work. This
 * costs constant time, and the distance it returns is measured from the ray's origin exactly
 * as a hit's own distance is, so the two compare as they stand.
 *
 * **A card at the silhouette is not hidden.** The ray to one grazes past the sphere instead
 * of crossing it, and those rim cards are the visible ones: the question being asked is
 * occlusion, so they are kept. Culling by which way a card faces would not keep them — a
 * card's front looks outward along the ring, so every card from roughly the rim backwards is
 * back-facing, and a facing test would throw away cards out at the rim that are on screen.
 * That is why the cards stay DoubleSide, and why this is a sphere test rather than a normal.
 */
export function planetHidesCard(cameraPosition, cardPoint, planetCentre, planetRadius) {

    _toCard.subVectors(cardPoint, cameraPosition);
    const distance = _toCard.length();

    // A degenerate segment has nothing in between to hide the card.
    if (distance === 0) return false;

    _ray.origin.copy(cameraPosition);
    _ray.direction.copy(_toCard).divideScalar(distance);
    _sphere.set(planetCentre, planetRadius);

    const entry = _ray.intersectSphere(_sphere, _entry);

    // `intersectSphere` answers with the *far* crossing when the ray begins inside the sphere.
    // That cannot arise here, because the camera lands outside the body it framed; if it did,
    // the answer would simply be measured against that crossing instead, so a card nearer to
    // the camera than it stays selectable. There is nothing to see from inside a planet either
    // way.
    return entry !== null && _ray.origin.distanceTo(entry) < distance;
}
